"use client";

import * as React from "react";
import { Eye, EyeOff, Lock, Mail, User } from "lucide-react";
import { Button, Input } from "@gerama/ui";
import { AuthLogo, FormError } from "./auth-bits";

export interface RegisterValues {
  name: string;
  email: string;
  password: string;
  passwordConfirm: string;
}

export function AuthRegisterForm({
  onSubmit,
  onBack,
  isLoading,
  error,
}: {
  onSubmit: (values: RegisterValues) => void;
  onBack: () => void;
  isLoading: boolean;
  error?: string;
}) {
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [fieldError, setFieldError] = React.useState<string | null>(null);

  return (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        const values: RegisterValues = {
          name: String(data.get("name") ?? "").trim(),
          email: String(data.get("email") ?? "").trim(),
          password: String(data.get("password") ?? ""),
          passwordConfirm: String(data.get("passwordConfirm") ?? ""),
        };
        if (!values.name) {
          setFieldError("نام الزامی است");
          return;
        }
        if (values.password.length < 8) {
          setFieldError("رمز عبور حداقل ۸ کاراکتر است.");
          return;
        }
        if (values.password !== values.passwordConfirm) {
          setFieldError("تکرار رمز با رمز عبور یکسان نیست");
          return;
        }
        setFieldError(null);
        onSubmit(values);
      }}
    >
      <AuthLogo />
      <p className="text-muted-foreground mt-5 mb-4 text-sm">
        برای تکمیل ثبت‌نام، نام، ایمیل (اختیاری) و رمز عبور دلخواه خود را وارد کنید.
      </p>
      <Field icon={<User className="size-4" />} name="name" placeholder="نام خود را وارد کنید" autoComplete="name" autoFocus disabled={isLoading} />
      <Field icon={<Mail className="size-4" />} name="email" type="email" placeholder="ایمیل (اختیاری)" autoComplete="email" disabled={isLoading} />
      <Field
        icon={<Lock className="size-4" />}
        name="password"
        type={showPassword ? "text" : "password"}
        placeholder="رمز عبور"
        autoComplete="new-password"
        disabled={isLoading}
        onToggle={() => setShowPassword((value) => !value)}
        shown={showPassword}
      />
      <Field
        icon={<Lock className="size-4" />}
        name="passwordConfirm"
        type={showConfirm ? "text" : "password"}
        placeholder="تکرار رمز عبور"
        autoComplete="new-password"
        disabled={isLoading}
        onToggle={() => setShowConfirm((value) => !value)}
        shown={showConfirm}
      />
      <FormError message={fieldError ?? error} />
      <Button type="submit" className="h-11 w-full rounded-lg" disabled={isLoading}>
        {isLoading ? "..." : "ادامه"}
      </Button>
      <Button type="button" variant="ghost" className="mt-4 h-11 w-full font-light" disabled={isLoading} onClick={onBack}>
        بازگشت
      </Button>
    </form>
  );
}

export function AuthNewPasswordForm({
  onSubmit,
  onBack,
  isLoading,
  error,
}: {
  onSubmit: (values: { password: string; passwordConfirm: string }) => void;
  onBack: () => void;
  isLoading: boolean;
  error?: string;
}) {
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [fieldError, setFieldError] = React.useState<string | null>(null);

  return (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        const password = String(data.get("password") ?? "");
        const passwordConfirm = String(data.get("passwordConfirm") ?? "");
        if (password.length < 8) {
          setFieldError("رمز عبور حداقل ۸ کاراکتر است.");
          return;
        }
        if (password !== passwordConfirm) {
          setFieldError("تکرار رمز با رمز عبور یکسان نیست");
          return;
        }
        setFieldError(null);
        onSubmit({ password, passwordConfirm });
      }}
    >
      <AuthLogo />
      <p className="text-muted-foreground mt-5 mb-4 text-sm">رمز عبور جدید را وارد کنید</p>
      <Field
        icon={<Lock className="size-4" />}
        name="password"
        type={showPassword ? "text" : "password"}
        placeholder="رمز عبور"
        autoComplete="new-password"
        autoFocus
        disabled={isLoading}
        onToggle={() => setShowPassword((value) => !value)}
        shown={showPassword}
      />
      <Field
        icon={<Lock className="size-4" />}
        name="passwordConfirm"
        type={showConfirm ? "text" : "password"}
        placeholder="تکرار رمز عبور"
        autoComplete="new-password"
        disabled={isLoading}
        onToggle={() => setShowConfirm((value) => !value)}
        shown={showConfirm}
      />
      <FormError message={fieldError ?? error} />
      <Button type="submit" className="h-11 w-full rounded-lg" disabled={isLoading}>
        {isLoading ? "..." : "ادامه"}
      </Button>
      <Button type="button" variant="ghost" className="mt-4 h-11 w-full font-light" disabled={isLoading} onClick={onBack}>
        بازگشت
      </Button>
    </form>
  );
}

function Field({
  icon,
  name,
  type = "text",
  placeholder,
  autoComplete,
  autoFocus,
  disabled,
  onToggle,
  shown,
}: {
  icon: React.ReactNode;
  name: string;
  type?: string;
  placeholder: string;
  autoComplete?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  onToggle?: () => void;
  shown?: boolean;
}) {
  return (
    <div className="relative mb-4">
      <span className="text-muted-foreground absolute top-1/2 start-2.5 flex -translate-y-1/2">{icon}</span>
      <Input
        name={name}
        type={type}
        placeholder={placeholder}
        aria-label={placeholder}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        disabled={disabled}
        className="h-11 rounded-lg bg-muted/50 ps-9 pe-10"
      />
      {onToggle ? (
        <button
          type="button"
          onClick={onToggle}
          className="text-muted-foreground hover:text-foreground absolute top-1/2 end-2.5 -translate-y-1/2 rounded p-1"
          aria-label={shown ? "پنهان کردن رمز" : "نمایش رمز"}
        >
          {shown ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      ) : null}
    </div>
  );
}
